import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  TextField,
  Paper,
  Alert,
  Autocomplete,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Palette as PaletteIcon,
  TextFields as TextIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { pdfTemplatesAPI } from '../services/api';

const TEMPLATE_TYPES = [
  { value: 'attestation', label: 'Attestation' },
  { value: 'invoice', label: 'Facture' },
  { value: 'proforma', label: 'Facture Proforma' },
  { value: 'recu', label: 'Reçu' },
  { value: 'avoir', label: 'Facture Avoir' },
];

const DEFAULT_TEMPLATES = {
  attestation: {
    template_type: 'attestation',
    is_active: true,
    header_title: 'ATTESTATION',
    show_logo: true,
    show_company_info: true,
    header_bg_color: '#4a4a4a',
    header_text_color: '#ffffff',
    title_color: '#1a1a1a',
    table_header_bg: '#4a4a4a',
    table_header_text: '#ffffff',
    table_alt_row: '#f5f5f5',
    intro_text: 'Je soussigné, {manager_name}, Gérant de la société {company_name}, certifie que la société {client_name} dispose d\'un parc de véhicules conformément aux termes convenus.',
    body_text: 'Le parc de véhicules de la société {client_name} est composé de {vehicle_count} véhicules couverts par notre assurance tous risques. Les véhicules sont maintenus en bon état de fonctionnement et respectent les normes de sécurité en vigueur.',
    table_header_text_field: 'Détail des véhicules couverts :',
    footer_text: 'Cette attestation est délivrée pour servir et valoir ce que de droit. En cas de besoin, elle peut être présentée aux autorités compétentes.',
    signature_text: 'Le Gérant',
    payment_terms: '',
    bank_details_text: '',
    platform_name: '',
    margin_top: 2.0,
    margin_bottom: 2.0,
    margin_left: 2.0,
    margin_right: 2.0,
    title_font_size: 18,
    body_font_size: 11,
    table_font_size: 10,
    footer_font_size: 8,
  },
  invoice: {
    template_type: 'invoice',
    is_active: true,
    header_title: 'FACTURE',
    show_logo: true,
    show_company_info: true,
    header_bg_color: '#4a4a4a',
    header_text_color: '#ffffff',
    title_color: '#1a1a1a',
    table_header_bg: '#4a4a4a',
    table_header_text: '#ffffff',
    table_alt_row: '#f5f5f5',
    intro_text: '',
    body_text: '',
    table_header_text_field: '',
    footer_text: '',
    signature_text: 'Le Gérant',
    payment_terms: 'Paiement à réception de facture. Tout retard de paiement entraînera des pénalités de retard au taux légal.',
    bank_details_text: 'Banque: [Nom de la banque]\nIBAN: [Votre IBAN]\nSWIFT: [Votre SWIFT]',
    platform_name: '',
    margin_top: 2.0,
    margin_bottom: 2.0,
    margin_left: 2.0,
    margin_right: 2.0,
    title_font_size: 18,
    body_font_size: 11,
    table_font_size: 10,
    footer_font_size: 8,
  },
  proforma: {
    template_type: 'proforma',
    is_active: true,
    header_title: 'FACTURE PROFORMA',
    show_logo: true,
    show_company_info: true,
    header_bg_color: '#4a4a4a',
    header_text_color: '#ffffff',
    title_color: '#1a1a1a',
    table_header_bg: '#4a4a4a',
    table_header_text: '#ffffff',
    table_alt_row: '#f5f5f5',
    intro_text: '',
    body_text: '',
    table_header_text_field: '',
    footer_text: '',
    signature_text: 'Le Gérant',
    payment_terms: 'Cette facture proforma n\'est pas une facture exigible. Elle sert de devis pour la commande référencée.',
    bank_details_text: 'Banque: [Nom de la banque]\nIBAN: [Votre IBAN]\nSWIFT: [Votre SWIFT]',
    platform_name: '',
    margin_top: 2.0,
    margin_bottom: 2.0,
    margin_left: 2.0,
    margin_right: 2.0,
    title_font_size: 18,
    body_font_size: 11,
    table_font_size: 10,
    footer_font_size: 8,
  },
  recu: {
    template_type: 'recu',
    is_active: true,
    header_title: 'REÇU',
    show_logo: true,
    show_company_info: true,
    header_bg_color: '#2e7d32',
    header_text_color: '#ffffff',
    title_color: '#2e7d32',
    table_header_bg: '#2e7d32',
    table_header_text: '#ffffff',
    table_alt_row: '#f5f5f5',
    intro_text: '',
    body_text: '',
    table_header_text_field: '',
    footer_text: 'Ce reçu est délivré pour servir et valoir ce que de droit.',
    signature_text: 'Le Gérant',
    payment_terms: '',
    bank_details_text: '',
    platform_name: '',
    margin_top: 2.0,
    margin_bottom: 2.0,
    margin_left: 2.0,
    margin_right: 2.0,
    title_font_size: 18,
    body_font_size: 11,
    table_font_size: 10,
    footer_font_size: 8,
  },
  avoir: {
    template_type: 'avoir',
    is_active: true,
    header_title: 'FACTURE AVOIR',
    show_logo: true,
    show_company_info: true,
    header_bg_color: '#d32f2f',
    header_text_color: '#ffffff',
    title_color: '#d32f2f',
    table_header_bg: '#d32f2f',
    table_header_text: '#ffffff',
    table_alt_row: '#f5f5f5',
    intro_text: '',
    body_text: '',
    table_header_text_field: '',
    footer_text: 'Cette facture avoir viendra en déduction de vos prochains paiements.',
    signature_text: 'Le Gérant',
    payment_terms: '',
    bank_details_text: '',
    platform_name: '',
    margin_top: 2.0,
    margin_bottom: 2.0,
    margin_left: 2.0,
    margin_right: 2.0,
    title_font_size: 18,
    body_font_size: 11,
    table_font_size: 10,
    footer_font_size: 8,
  },
};

function PDFTemplateEditorPage() {
  const [templateType, setTemplateType] = useState('attestation');
  const [template, setTemplate] = useState({ ...DEFAULT_TEMPLATES.attestation });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  
  // Debounce timer for auto-preview
  const previewTimeoutRef = useRef(null);
  
  // Stable reference for template to avoid infinite loops
  const templateRef = useRef(template);
  templateRef.current = template;

  // Load template when type changes
  useEffect(() => {
    loadTemplate(templateType);
  }, [templateType]);

  const generatePreview = useCallback(async () => {
    try {
      setPreviewing(true);
      
      const response = await pdfTemplatesAPI.preview(templateRef.current);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Revoke previous URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      
      setPdfPreviewUrl(url);
    } catch (err) {
      console.error('Error generating preview:', err);
      // Don't show error for auto-preview failures
    } finally {
      setPreviewing(false);
    }
  }, [pdfPreviewUrl]);

  // Auto-generate preview when template changes
  useEffect(() => {
    // Clear existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    
    // Set new timeout for auto-preview (500ms debounce)
    previewTimeoutRef.current = setTimeout(() => {
      generatePreview();
    }, 500);
    
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [template]);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  const loadTemplate = async (type) => {
    try {
      setLoading(true);
      setError(null);
      const response = await pdfTemplatesAPI.getByType(type);
      // Preserve default text values if API returns empty strings
      const apiData = response.data;
      const defaultTemplate = DEFAULT_TEMPLATES[type] || DEFAULT_TEMPLATES.attestation;
      const mergedTemplate = {
        ...defaultTemplate,
        ...apiData,
        template_type: type,
        // Ensure text fields have meaningful content if API returns empty
        intro_text: apiData.intro_text || defaultTemplate.intro_text,
        body_text: apiData.body_text || defaultTemplate.body_text,
        table_header_text_field: apiData.table_header_text_field || defaultTemplate.table_header_text_field,
        footer_text: apiData.footer_text || defaultTemplate.footer_text,
      };
      setTemplate(mergedTemplate);
    } catch (err) {
      console.error('Error loading template:', err);
      const defaultTemplate = DEFAULT_TEMPLATES[type] || DEFAULT_TEMPLATES.attestation;
      setTemplate({ ...defaultTemplate, template_type: type });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setTemplate(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      if (template.id) {
        await pdfTemplatesAPI.update(template.id, template);
      } else {
        const response = await pdfTemplatesAPI.create(template);
        setTemplate(prev => ({ ...prev, id: response.data.id }));
      }
      
      setSuccess('Template sauvegardé avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Get selected template type object for Autocomplete
  const selectedType = TEMPLATE_TYPES.find(t => t.value === templateType) || TEMPLATE_TYPES[0];

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Éditeur de Templates PDF
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
        >
          Sauvegarder
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Template Type Selector with Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          value={selectedType}
          onChange={(e, newValue) => {
            if (newValue) setTemplateType(newValue.value);
          }}
          options={TEMPLATE_TYPES}
          getOptionLabel={(option) => option.label}
          renderOption={(props, option) => (
            <li {...props}>
              {option.label}
              {template.id && template.template_type === option.value && (
                <Chip label="Configuré" size="small" color="success" sx={{ ml: 2 }} />
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Type de document" />
          )}
          disableClearable
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Editor Panel - Left side */}
          <Paper sx={{ p: 2, maxHeight: '80vh', overflow: 'auto', flex: previewFullscreen ? 0 : 1, display: previewFullscreen ? 'none' : 'block' }}>
            <Typography variant="h6" gutterBottom>Édition</Typography>
            <Divider sx={{ mb: 2 }} />
              
              {/* Header Settings */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography>En-tête</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Titre du document"
                        value={template.header_title}
                        onChange={(e) => handleFieldChange('header_title', e.target.value)}
                        helperText="Ex: ATTESTATION, Facture, etc."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Nom de la plateforme"
                        value={template.platform_name}
                        onChange={(e) => handleFieldChange('platform_name', e.target.value)}
                        helperText="Nom personnalisé pour les descriptions d'abonnement (laisser vide pour utiliser le nom de l'entreprise)"
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.show_logo}
                            onChange={(e) => handleFieldChange('show_logo', e.target.checked)}
                          />
                        }
                        label="Afficher le logo"
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={template.show_company_info}
                            onChange={(e) => handleFieldChange('show_company_info', e.target.checked)}
                          />
                        }
                        label="Afficher infos société"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Colors */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <PaletteIcon sx={{ mr: 1 }} />
                  <Typography>Couleurs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Fond en-tête"
                          value={template.header_bg_color}
                          onChange={(e) => handleFieldChange('header_bg_color', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.header_bg_color}
                          onChange={(e) => handleFieldChange('header_bg_color', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Texte en-tête"
                          value={template.header_text_color}
                          onChange={(e) => handleFieldChange('header_text_color', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.header_text_color}
                          onChange={(e) => handleFieldChange('header_text_color', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Couleur titre"
                          value={template.title_color}
                          onChange={(e) => handleFieldChange('title_color', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.title_color}
                          onChange={(e) => handleFieldChange('title_color', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Fond en-tête tableau"
                          value={template.table_header_bg}
                          onChange={(e) => handleFieldChange('table_header_bg', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.table_header_bg}
                          onChange={(e) => handleFieldChange('table_header_bg', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Texte en-tête tableau"
                          value={template.table_header_text}
                          onChange={(e) => handleFieldChange('table_header_text', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.table_header_text}
                          onChange={(e) => handleFieldChange('table_header_text', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Ligne alternée"
                          value={template.table_alt_row}
                          onChange={(e) => handleFieldChange('table_alt_row', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <input
                          type="color"
                          value={template.table_alt_row}
                          onChange={(e) => handleFieldChange('table_alt_row', e.target.value)}
                          style={{
                            width: 50,
                            height: 40,
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Text Content */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <TextIcon sx={{ mr: 1 }} />
                  <Typography>Contenu texte</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Placeholders: {'{company_name}'}, {'{manager_name}'}, {'{client_name}'}, {'{date}'}, {'{vehicle_count}'}, {'{document_number}'}
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Texte d'introduction"
                        value={template.intro_text}
                        onChange={(e) => handleFieldChange('intro_text', e.target.value)}
                        placeholder="Je soussigné, {manager_name}, Gérant de la société {company_name}, certifie que la société {client_name} dispose d'un parc de véhicules conformément aux termes du contrat n°{contract_number}."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Corps du texte"
                        value={template.body_text}
                        onChange={(e) => handleFieldChange('body_text', e.target.value)}
                        placeholder="Le parc de véhicules de la société {client_name} est composé de {vehicle_count} véhicules couverts par notre assurance tous risques. Les véhicules sont maintenus en bon état de fonctionnement et respectent les normes de sécurité en vigueur."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Texte avant tableau"
                        value={template.table_header_text_field}
                        onChange={(e) => handleFieldChange('table_header_text_field', e.target.value)}
                        placeholder="Détail des véhicules couverts :"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Texte de pied de page"
                        value={template.footer_text}
                        onChange={(e) => handleFieldChange('footer_text', e.target.value)}
                        placeholder="Cette attestation est délivrée pour servir et valoir ce que de droit. En cas de besoin, elle peut être présentée aux autorités compétentes."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Texte signature"
                        value={template.signature_text}
                        onChange={(e) => handleFieldChange('signature_text', e.target.value)}
                        placeholder="Le Gérant"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Layout */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography>Mise en page</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" gutterBottom>Marges (cm)</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Haut"
                        value={template.margin_top}
                        onChange={(e) => handleFieldChange('margin_top', parseFloat(e.target.value) || 2)}
                        inputProps={{ step: 0.5, min: 0.5, max: 5 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Bas"
                        value={template.margin_bottom}
                        onChange={(e) => handleFieldChange('margin_bottom', parseFloat(e.target.value) || 2)}
                        inputProps={{ step: 0.5, min: 0.5, max: 5 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Gauche"
                        value={template.margin_left}
                        onChange={(e) => handleFieldChange('margin_left', parseFloat(e.target.value) || 2)}
                        inputProps={{ step: 0.5, min: 0.5, max: 5 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Droite"
                        value={template.margin_right}
                        onChange={(e) => handleFieldChange('margin_right', parseFloat(e.target.value) || 2)}
                        inputProps={{ step: 0.5, min: 0.5, max: 5 }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Tailles de police</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Titre"
                        value={template.title_font_size}
                        onChange={(e) => handleFieldChange('title_font_size', parseInt(e.target.value) || 18)}
                        inputProps={{ min: 10, max: 36 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Corps"
                        value={template.body_font_size}
                        onChange={(e) => handleFieldChange('body_font_size', parseInt(e.target.value) || 11)}
                        inputProps={{ min: 8, max: 16 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Tableau"
                        value={template.table_font_size}
                        onChange={(e) => handleFieldChange('table_font_size', parseInt(e.target.value) || 10)}
                        inputProps={{ min: 6, max: 14 }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Pied de page"
                        value={template.footer_font_size}
                        onChange={(e) => handleFieldChange('footer_font_size', parseInt(e.target.value) || 8)}
                        inputProps={{ min: 6, max: 12 }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Invoice-specific */}
              {(templateType === 'invoice' || templateType === 'proforma') && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Options Facture</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Conditions de paiement"
                          value={template.payment_terms}
                          onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Coordonnées bancaires"
                          value={template.bank_details_text}
                          onChange={(e) => handleFieldChange('bank_details_text', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}
          </Paper>

          {/* Live Preview Panel - Right side */}
          <Paper sx={{ 
            p: 2, 
            height: previewFullscreen ? '90vh' : '80vh', 
            display: 'flex', 
            flexDirection: 'column',
            flex: previewFullscreen ? 1 : 1,
            minWidth: previewFullscreen ? '100%' : 'auto'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Aperçu en direct</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {previewing && <CircularProgress size={20} />}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={previewFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  onClick={() => setPreviewFullscreen(!previewFullscreen)}
                >
                  {previewFullscreen ? 'Réduire' : 'Agrandir'}
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {pdfPreviewUrl ? (
              <Box sx={{ flex: 1 }}>
                <iframe
                  src={pdfPreviewUrl}
                  width="100%"
                  height="100%"
                  style={{ border: '1px solid #ccc', borderRadius: 4 }}
                  title="PDF Preview"
                />
              </Box>
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">
                  {previewing ? 'Génération en cours...' : 'Chargement de l\'aperçu...'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
}

export default PDFTemplateEditorPage;
